import {
  MSG as BASE_MSG,
  isPasteTarget,
  isPasteRequestMessage,
  isPasteResultMessage,
  isEmbeddedMessage,
  isPmsDetectedMessage,
} from '@hellopearl/iso-voice-models';
import { isObject } from '@hellopearl/iso-voice-models';

export type {
  PasteRequestMessage,
  PasteResultMessage,
  PmsDetectedMessage,
  EmbeddedMessage,
} from '@hellopearl/iso-voice-models';

export const MSG = {
  ...BASE_MSG,

  // popup <-> content script (extension-internal)
  GET_WIDGET_STATE: 'SCRIBE_GET_WIDGET_STATE',
  SET_WIDGET_VISIBLE: 'SCRIBE_SET_WIDGET_VISIBLE',
  SET_USER_ROLE: 'PEARL_SET_USER_ROLE',

  // content script -> background (extension-internal)
  OPEN_TAB: 'SCRIBE_OPEN_TAB',
  VOICE_READY_TO_SHOW: 'SCRIBE_VOICE_READY_TO_SHOW',
  LOGIN_DETECTED: 'PEARL_LOGIN_DETECTED',
  AUTH_TOKEN_FOUND: 'PEARL_AUTH_TOKEN_FOUND',
  PROBE_TOKEN: 'PEARL_PROBE_TOKEN',
  FETCH_CONFIG: 'PEARL_FETCH_CONFIG',

  // Voice session persistence across navigations (content <-> background)
  VOICE_SESSION_SET: 'PEARL_VOICE_SESSION_SET',
  VOICE_SESSION_GET: 'PEARL_VOICE_SESSION_GET',

  // Content script -> background: open the Voice side panel
  OPEN_SIDE_PANEL: 'PEARL_OPEN_SIDE_PANEL',
  PING: 'PEARL_PING',
  // Content -> background: may Voice open in the page when Chrome cannot show
  // the side panel (app/popup window, SCRIBE-1326)?
  VOICE_IN_PAGE_GATE: 'PEARL_VOICE_IN_PAGE_GATE',
  // Background -> content: the in-page Voice panel was minimized; unmount it
  VOICE_IN_PAGE_HIDE: 'PEARL_VOICE_IN_PAGE_HIDE',

  // Side panel -> background: restart Dock into the downloaded update
  APPLY_EXTENSION_UPDATE: 'PEARL_APPLY_EXTENSION_UPDATE',
  // Background -> side panel: is the dentist working in the panel?
  SIDE_PANEL_FOCUS_QUERY: 'PEARL_SIDE_PANEL_FOCUS_QUERY',
  // Background -> offscreen: the recording engine's phase (idle … stopping)
  RECORDING_PHASE_QUERY: 'PEARL_RECORDING_PHASE_QUERY',

  // Content → background: live-pms perio recording (no side panel)
  START_LIVE_PERIO: 'PEARL_START_LIVE_PERIO',
  STOP_LIVE_PERIO: 'PEARL_STOP_LIVE_PERIO',

  // Side panel → active tab content script: request PMS re-detection
  REQUEST_PMS_DETECT: 'SCRIBE_REQUEST_PMS_DETECT',
  // Background → content: snapshot the active PMS patient before recording.
  CAPTURE_WRITEBACK_TARGET: 'SCRIBE_CAPTURE_WRITEBACK_TARGET',

  // Background → offscreen: ask for microphone permission state
  MIC_PERMISSION_QUERY: 'PEARL_MIC_PERMISSION_QUERY',

  // Sign-out: popup → background clears all auth state; background → all tabs
  SIGN_OUT: 'PEARL_SIGN_OUT',
  SIGNED_OUT: 'PEARL_SIGNED_OUT',

  // Explicit user login: clears the signed-out latch so AUTH_TOKEN_FOUND is accepted again
  BEGIN_USER_LOGIN: 'PEARL_BEGIN_USER_LOGIN',

  // Toolbar / content → open the FAB Dock login card + SO iframe on the active PMS tab
  OPEN_FAB_LOGIN: 'PEARL_OPEN_FAB_LOGIN',

  // Auth cache probe (hellopearl-bridge / content script → background)
  CHECK_AUTH_CACHE: 'CHECK_AUTH_CACHE',
  // Content → background: a reconnect probe found no Pearl login; the
  // background clears the signed-in marker unless it holds a valid token.
  AUTH_RECONNECT_GAVE_UP: 'PEARL_AUTH_RECONNECT_GAVE_UP',

  // Popup → background: force-refresh Denticon modules-in-tabs org default
  REFRESH_DENTICON_MODULES_IN_TABS: 'PEARL_REFRESH_DENTICON_MODULES_IN_TABS',

  // Voice URL cache (iframe orchestrator ↔ background)
  CACHE_VOICE_URL: 'CACHE_VOICE_URL',
  GET_CACHED_VOICE_URL: 'GET_CACHED_VOICE_URL',

  // Background → content: show a toast in the shell UI
  SHOW_TOAST: 'PEARL_SHOW_TOAST',

  // Content → background: allowlisted Denticon overview remint (never a URL)
  DENTICON_REMINT: 'PEARL_DENTICON_REMINT',
  // Denticon tab → background: may this office write treatment plans (SCRIBE-1082)
  DENTICON_PLAN_GATE: 'PEARL_DENTICON_PLAN_GATE',
  // Background → Denticon chart tab: refresh Restorative Chart in place after a plan write (SCRIBE-1082)

  // Curve tab → background: may this office write treatment plans (SCRIBE-1182)
  CURVE_PLAN_GATE: 'PEARL_CURVE_PLAN_GATE',

  // Isolated EPMS content → background: finished notes for the Voice Note menu
  EPMS_VOICE_NOTES: 'PEARL_EPMS_VOICE_NOTES',

  // Background → EPMS chart tab: write confirmed plan lines (SCRIBE-1171)
  EPMS_PLAN_WRITE: 'PEARL_EPMS_PLAN_WRITE',
  // EPMS chart tab → background: may this office write plans (SCRIBE-1171)
  EPMS_PLAN_GATE: 'PEARL_EPMS_PLAN_GATE',
  // Background → EPMS chart tab: import/undo Second Opinion X-ray chart findings (SCRIBE-1402/1403)
  EPMS_XRAY_CHART_IMPORT: 'PEARL_EPMS_XRAY_CHART_IMPORT',

  // Dock launcher mode (DOCK-174): content -> background asks which launcher
  // the org gets, then reports the resolved mode so the toolbar follows it.
  GET_DOCK_LAUNCHER: 'PEARL_GET_DOCK_LAUNCHER',
  DOCK_LAUNCHER_CHANGED: 'PEARL_DOCK_LAUNCHER_CHANGED',

  // Side-panel Dock (DOCK-174): menu -> background opens a product window;
  // background -> PMS content script resolves the product URL for that tab.
  OPEN_PRODUCT_WINDOW: 'PEARL_OPEN_PRODUCT_WINDOW',
  RESOLVE_PRODUCT_URL: 'PEARL_RESOLVE_PRODUCT_URL',

  // Structured log relay
  LOG_BATCH: 'PEARL_LOG_BATCH',

  // Product analytics event relay (content/offscreen/sidepanel/popup → background)
  EVENT_BATCH: 'PEARL_EVENT_BATCH',
} as const;

export {
  isObject,
  isPasteTarget,
  isPasteRequestMessage,
  isPasteResultMessage,
  isEmbeddedMessage,
  isPmsDetectedMessage,
};

export type { PasteTarget } from '@hellopearl/iso-voice-models';
